import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { BackButton } from '@/components/ui/back-button';
import { Button } from '@/components/ui/button';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { buildLegalDocumentIframeSrcDoc } from '@/components/legal/legal-document-rendering';
import { myLegalAgreementsApi } from '@/lib/pillarApi';
import type { MyLegalAgreement } from '@pillar-portal/api-types';
import { SYSTEM_DISPLAY_TIME_ZONE, formatTimestampForMerchantUi } from '@pillar-portal/api-types';

const queryKeyPrefix = ['myLegalAgreements'] as const;

function AcceptedText({ agreement }: { readonly agreement: MyLegalAgreement }) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['myLegalAgreement', agreement.acceptance_kind, agreement.acceptance_uuid],
        queryFn: async () =>
            await myLegalAgreementsApi.getMyLegalAgreement(
                agreement.acceptance_kind,
                agreement.acceptance_uuid
            ),
        staleTime: Infinity,
    });
    if (isLoading) return <p>Loading the text you accepted...</p>;
    if (isError || data === undefined) {
        return <p role="alert">The text you accepted could not be loaded.</p>;
    }
    return (
        <iframe
            title={`Text of ${agreement.legal_document_title}`}
            sandbox=""
            className="mt-2 h-[60vh] w-full rounded border"
            srcDoc={buildLegalDocumentIframeSrcDoc({
                legalDocumentContentType: data.accepted_content.legal_document_content_type,
                legalDocumentText: data.accepted_content.legal_document_text,
            })}
        />
    );
}

function describeAgreement(agreement: MyLegalAgreement): string {
    const on = formatTimestampForMerchantUi(agreement.accepted_ts, SYSTEM_DISPLAY_TIME_ZONE);
    if (agreement.acceptance_kind === 'organization') {
        return `Signed for ${agreement.organization.organization_name} on ${on}`;
    }
    return agreement.organization === null
        ? `Accepted on ${on}`
        : `Accepted as a member of ${agreement.organization.organization_name} on ${on}`;
}

/** Every agreement the user accepted or signed, each opening the exact text. */
export function MyAgreementsContent() {
    const [openAcceptanceUuid, setOpenAcceptanceUuid] = useState<string | null>(null);
    return (
        <div>
            <BackButton href="/profile" tooltip="Back to Profile" />
            <div className="max-w-3xl">
                <InfiniteScrollList<MyLegalAgreement, string, typeof queryKeyPrefix>
                    pageTitle="My agreements"
                    queryPlaceholder=""
                    showQueryInput={false}
                    queryKeyPrefix={queryKeyPrefix}
                    pageSize={25}
                    fetchPage={async (params) => {
                        const response = await myLegalAgreementsApi.searchMyLegalAgreementsPage({
                            limit: params.limit,
                            cursor: params.cursor,
                        });
                        return {
                            items: response.my_legal_agreements,
                            pagination: response.pagination,
                        };
                    }}
                    parseCursorParam={(pageParam: unknown) =>
                        pageParam === null || pageParam === undefined
                            ? undefined
                            : z.string().parse(pageParam)
                    }
                    stateCopy={{
                        loadingLabel: 'Loading your agreements...',
                        emptyLabel: 'You have not accepted or signed any agreement.',
                        endOfListLabel: 'End of your agreements.',
                        genericErrorLabel: 'Unable to load your agreements.',
                    }}
                    renderItems={({ items }) => (
                        <div className="space-y-2">
                            {items.map((agreement) => {
                                const isOpen = openAcceptanceUuid === agreement.acceptance_uuid;
                                return (
                                    <div
                                        key={agreement.acceptance_uuid}
                                        className="rounded border p-3 text-sm"
                                    >
                                        <div className="font-medium">
                                            {agreement.legal_document_title}
                                        </div>
                                        <div>{describeAgreement(agreement)}</div>
                                        {agreement.withdrawn_ts === null ? null : (
                                            <div className="text-amber-800">
                                                Withdrawn by an administrator on{' '}
                                                {formatTimestampForMerchantUi(
                                                    agreement.withdrawn_ts,
                                                    SYSTEM_DISPLAY_TIME_ZONE
                                                )}
                                                ; it no longer counts.
                                            </div>
                                        )}
                                        {agreement.accepted_text_matches_current ? null : (
                                            <div className="text-gray-600">
                                                The document has changed since. You can still read
                                                the text you accepted.
                                            </div>
                                        )}
                                        <Button
                                            variant="outline"
                                            className="mt-2"
                                            onClick={() =>
                                                setOpenAcceptanceUuid(
                                                    isOpen ? null : agreement.acceptance_uuid
                                                )
                                            }
                                        >
                                            {isOpen ? 'Hide text' : 'Read the text you accepted'}
                                        </Button>
                                        {isOpen ? <AcceptedText agreement={agreement} /> : null}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                />
            </div>
        </div>
    );
}

export default function MyAgreementsPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <MyAgreementsContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
